/**
 * Cross-tab session synchronisation.
 *
 * Signing out in one tab must sign out every tab. Without this, a user who
 * signs out on their laptop leaves their other tab authenticated — and, worse,
 * still able to read cached tenant data on screen.
 *
 * This is a SECURITY control, not a UX convenience. It is broadcast over
 * BroadcastChannel, which is same-origin only; a cross-origin page cannot
 * receive or forge these messages.
 */

export type AuthSyncMessage =
  | { readonly type: 'signed-out' }
  | { readonly type: 'refreshed'; readonly at: number };

const CHANNEL_NAME = 'mana-locals:auth-sync';

type ChannelFactory = (name: string) => BroadcastChannel | null;

function defaultFactory(name: string): BroadcastChannel | null {
  // Absent in some test environments and in older browsers. Absent support is
  // a degraded experience, never a crash.
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name);
}

export function broadcastAuthSync(
  message: AuthSyncMessage,
  create: ChannelFactory = defaultFactory,
): void {
  const channel = create(CHANNEL_NAME);
  if (!channel) return;

  try {
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

/**
 * Subscribes to auth messages from other tabs.
 *
 * Returns an unsubscribe function. Always closes the channel on unsubscribe —
 * a leaked channel keeps receiving after the owning screen is gone, and jsdom
 * warns about it at teardown.
 */
export function subscribeAuthSync(
  handler: (message: AuthSyncMessage) => void,
  create: ChannelFactory = defaultFactory,
): () => void {
  const channel = create(CHANNEL_NAME);
  if (!channel) return () => {};

  const onMessage = (event: MessageEvent<AuthSyncMessage>) => {
    // Shape-check rather than trusting the channel. A same-origin page running
    // older code could post a shape this build does not understand.
    const data = event.data;
    if (typeof data !== 'object' || data === null) return;
    if (data.type === 'signed-out' || data.type === 'refreshed') {
      handler(data);
    }
  };

  channel.addEventListener('message', onMessage);

  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
}